import { type BaseEntity } from "@/shared/types";
import { z } from "zod";

// Status values aligned with backend (TicketStatus enum)
export type TicketStatus =
  | "OPEN"
  | "IN_PROGRESS"
  | "SCHEDULED"
  | "WAITING_FOR_SPARE_PARTS"
  | "QUESTION"
  | "RESOLVED"
  | "CLOSED";
export type TicketPriority = "LOW" | "MEDIUM" | "HIGH" | "CRITICAL";

export const ALL_TICKET_STATUSES: TicketStatus[] = [
  "OPEN",
  "IN_PROGRESS",
  "SCHEDULED",
  "WAITING_FOR_SPARE_PARTS",
  "QUESTION",
  "RESOLVED",
  "CLOSED",
];

/** Allowed next statuses from current (forward flow; CLOSED is final). */
export const ALLOWED_NEXT_STATUSES: Record<TicketStatus, TicketStatus[]> = {
  OPEN: ["IN_PROGRESS"],
  IN_PROGRESS: ["SCHEDULED", "WAITING_FOR_SPARE_PARTS", "QUESTION", "RESOLVED"],
  SCHEDULED: ["IN_PROGRESS"],
  WAITING_FOR_SPARE_PARTS: ["IN_PROGRESS"],
  QUESTION: ["IN_PROGRESS"],
  RESOLVED: ["IN_PROGRESS", "CLOSED"],
  CLOSED: [],
};

// Activity types for the unified activity stream
export type TicketActivityType =
  "COMMENT" | "STATUS_CHANGE" | "ASSIGNMENT" | "FORWARD_INFO" | "SYSTEM" | "EMAIL_OUT" | "EMAIL_IN";

export type TicketActivityVisibility = "PUBLIC" | "INTERNAL";

export interface Ticket extends BaseEntity {
  account_id: string; // tenant isolation
  branch_id: string; // required - branch where ticket originates
  asset_id?: string; // optional asset link
  asset_model_id?: string;
  asset_category_id?: string; // optional category
  problem_category_id?: string; // issue category
  /**
   * @deprecated Legacy field - use ticket_queues junction table via ticketQueueStore
   */
  queue_id?: string;
  ticketNumber?: string | null;
  /** Original requester parsed from a forwarded email; prefilled when sending mail from the ticket. */
  requesterEmail?: string | null;
  title: string;
  description?: string;
  status: TicketStatus;
  priority: TicketPriority;

  // US165: Status-dependent date fields
  scheduled_date?: string | null;
  waiting_spare_parts_date?: string | null;

  created_by_user_id: string;
  created_by_person_id?: string;
  assigned_to_user_id?: string;
  last_updated_by_user_id?: string;

  /** Attachments (when loaded via ticket-by-id GraphQL query) */
  attachments?: TicketAttachmentSummary[];

  /** Activities (when loaded via ticket-by-id GraphQL query) */
  activities?: TicketActivity[];

  /** Relations (when loaded via ticket-by-id GraphQL query) */
  location?: TicketLocationSummary | null;
  asset?: TicketAssetSummary | null;
  problemCategory?: TicketProblemCategorySummary | null;
  createdByUser?: TicketUserSummary | null;
  assignedToUser?: TicketUserSummary | null;
  lastUpdatedByUser?: TicketUserSummary | null;

  /** Whether the current authenticated user is a member of any queue linked to this ticket */
  isCurrentUserQueueMember?: boolean | null;

  /** Summaries of queues linked to this ticket (loaded via ticket-by-id GraphQL query) */
  ticketQueueSummaries?: TicketQueueSummary[];
}

export interface TicketQueueSummary {
  id: string;
  name: string;
  isDefault?: boolean | null;
}

export interface TicketLocationSummary {
  id: string;
  name: string;
}

export interface TicketAssetSummary {
  id: string;
  name: string;
}

export interface TicketProblemCategorySummary {
  id: string;
  name: string;
}

export interface TicketUserSummary {
  id: string;
  name: string;
}

export interface TicketAttachmentSummary {
  id: string;
  fileUrl: string;
  fileName?: string | null;
  mimeType?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface TicketActivity extends BaseEntity {
  account_id: string;
  ticket_id: string;
  /** Null for EMAIL_IN: the author is an external sender, not a LuryOS user. */
  created_by_user_id: string | null;
  /** User display name when loaded from API (ticket by id) */
  createdByUser?: { id: string; name: string } | null;
  type: TicketActivityType;
  visibility: TicketActivityVisibility;
  is_internal?: boolean;
  message?: string;
  /** Set on EMAIL_IN: the external address the reply came from. */
  externalAuthorEmail?: string | null;
  /** Set on EMAIL_OUT: the address the message was sent to. */
  sentToEmail?: string | null;
  meta?: {
    from?: string;
    to?: string;
    from_assignee?: string;
    to_assignee?: string;
    [key: string]: unknown;
  };
}

export interface TicketAttachment extends BaseEntity {
  account_id: string;
  ticket_id: string;
  activity_id?: string;
  uploaded_by_user_id: string;
  file_url: string;
  file_name?: string;
  mime_type?: string;
}

export const ticketFormSchema = z.object({
  branch_id: z.string().optional().or(z.literal("")),
  asset_id: z.string().optional().or(z.literal("")),
  problem_category_id: z.string().min(1, "Problem category is required"),
  /**
   * @deprecated Use ticket_queues junction table for 1:n queue assignment
   */
  queue_id: z.string().optional().or(z.literal("")),
  title: z.string().optional().or(z.literal("")),
  description: z.string().max(2000, "Description max 2000 chars").or(z.literal("")),
  priority: z.enum(["LOW", "MEDIUM", "HIGH", "CRITICAL"]).default("MEDIUM"),
  assigned_to_user_id: z.string().optional().or(z.literal("")),
});

export type TicketFormData = z.infer<typeof ticketFormSchema>;

/**
 * Creates a ticket form schema with conditional description validation.
 * For branch users, description is optional. For all other users, description is required.
 */
export const createTicketFormSchema = (isBranchUser: boolean) => {
  return z.object({
    branch_id: z.string().optional().or(z.literal("")),
    asset_id: z.string().optional().or(z.literal("")),
    problem_category_id: z.string().min(1, "Problem category is required"),
    /**
     * @deprecated Use ticket_queues junction table for 1:n queue assignment
     */
    queue_id: z.string().optional().or(z.literal("")),
    title: z.string().optional().or(z.literal("")),
    description: isBranchUser
      ? z.string().max(2000, "Description max 2000 chars").or(z.literal(""))
      : z.string().min(1, "Description is required").max(2000, "Description max 2000 chars"),
    priority: z.enum(["LOW", "MEDIUM", "HIGH", "CRITICAL"]).default("MEDIUM"),
    assigned_to_user_id: z.string().optional().or(z.literal("")),
  });
};

export interface StatusDateValidation {
  isValid: boolean;
  errorKey?: string;
}

export function validateStatusDates(
  status: TicketStatus,
  scheduledDate?: string | null,
  waitingSparePartsDate?: string | null
): StatusDateValidation {
  if (status === "SCHEDULED" && !scheduledDate) {
    return { isValid: false, errorKey: "tickets.validation.scheduledDateRequired" };
  }
  if (status === "WAITING_FOR_SPARE_PARTS" && !waitingSparePartsDate) {
    return { isValid: false, errorKey: "tickets.validation.waitingSparePartsDateRequired" };
  }
  return { isValid: true };
}

export const normalizeStatus = (status: string): TicketStatus => {
  const upper = status.toUpperCase().replace(/-/g, "_");
  if (ALL_TICKET_STATUSES.includes(upper as TicketStatus)) {
    return upper as TicketStatus;
  }
  return "OPEN";
};

export const normalizePriority = (priority: string): TicketPriority => {
  const upper = priority.toUpperCase();
  if (["LOW", "MEDIUM", "HIGH", "CRITICAL"].includes(upper)) {
    return upper as TicketPriority;
  }
  return "MEDIUM";
};

// Email template event types
export type QueueEmailTemplateEventType =
  "TICKET_CREATED" | "COMMENT_CREATED" | "COMMENT_REPLY" | "TICKET_FORWARDED" | "TICKET_REROUTED";

export interface TicketQueue extends BaseEntity {
  account_id?: string;
  tenant_id?: string;
  name: string;
  description?: string;
  is_default?: boolean;
  deleted_at?: Date | null;

  /** Backend-computed inbound email alias (local part only, e.g. "acme-it-support"). Read-only. */
  email_alias?: string | null;

  notification_emails?: string[];
  notification_email?: string;

  email_signature_html?: string;
  email_signature?: string;

  email_templates_html?: Partial<Record<QueueEmailTemplateEventType, string>>;

  email_subject_templates?: Partial<Record<QueueEmailTemplateEventType, string>>;

  // Legacy compatibility fields
  email_new_ticket?: string;
  email_new_comment?: string;
  email_comment_reply?: string;
  email_ticket_forwarded?: string;
  email_ticket_redirected?: string;

  /** Locations assigned to this Service Team (technical work area). */
  assigned_location_ids?: string[];
  assigned_locations?: Array<{ id: string; name: string }>;

  /** Problem Categories that route tickets to this Service Team (read-only at the team level). */
  problem_categories?: Array<{ id: string; name: string }>;
}

export interface TicketQueueUser extends BaseEntity {
  account_id?: string;
  queue_id: string;
  user_id: string;
}

export interface TicketQueueTicket extends BaseEntity {
  account_id?: string;
  queue_id: string;
  ticket_id: string;
}

export interface QueueEmailTemplate extends BaseEntity {
  account_id: string;
  queue_id: string;
  event_type: QueueEmailTemplateEventType;
  subject_template: string;
  template_html: string;
}

export const ticketQueueFormSchema = z.object({
  name: z
    .string()
    .trim()
    .min(1, "common:validation.nameRequired")
    .max(100, "common:validation.nameMaxLength100"),
  description: z
    .string()
    .max(500, "common:validation.descriptionMax500")
    .optional()
    .or(z.literal("")),
  is_default: z.boolean().default(false),

  notification_emails: z.array(z.string().email()).optional().default([]),
  notification_email: z
    .string()
    .trim()
    .email("common:validation.emailInvalid")
    .or(z.literal(""))
    .optional(),
  email_signature_html: z.string().optional().or(z.literal("")),

  user_ids: z.array(z.string()).default([]),
  location_ids: z.array(z.string()).default([]),

  // Legacy email template fields
  email_new_ticket: z.string().optional().or(z.literal("")),
  email_new_comment: z.string().optional().or(z.literal("")),
  email_comment_reply: z.string().optional().or(z.literal("")),
  email_ticket_forwarded: z.string().optional().or(z.literal("")),
  email_ticket_redirected: z.string().optional().or(z.literal("")),
  email_signature: z.string().optional().or(z.literal("")),
});

export type TicketQueueFormData = z.infer<typeof ticketQueueFormSchema>;

export const EMAIL_TEMPLATE_PLACEHOLDERS = [
  "{{ticketId}}",
  "{{ticketTitle}}",
  "{{ticketStatus}}",
  "{{ticketPriority}}",
  "{{branchName}}",
  "{{assetName}}",
  "{{problemCategory}}",
  "{{creatorName}}",
  "{{creatorEmail}}",
  "{{assigneeName}}",
  "{{serviceTeamNames}}",
  "{{ticketUrl}}",
  "{{commentText}}",
  "{{commentAuthor}}",
  "{{forwardedFrom}}",
  "{{forwardedTo}}",
] as const;

export type EmailTemplatePlaceholder = (typeof EMAIL_TEMPLATE_PLACEHOLDERS)[number];

export interface IssueCategory extends BaseEntity {
  account_id?: string;
  tenant_id?: string;
  name: string;
  description?: string;
  deleted_at?: Date | null;
  /** Whether new tickets can use this category (defaults true). */
  is_active?: boolean;
  queueCount?: number;
  assetModelCount?: number;
  queueNames?: string[];
  assetModelNames?: string[];
}

export type ProblemCategory = IssueCategory;

export interface IssueCategoryQueue extends BaseEntity {
  account_id?: string;
  category_id: string;
  queue_id: string;
  queue_name?: string;
}

export type ProblemCategoryQueue = IssueCategoryQueue;

export interface IssueCategoryAssetModel extends BaseEntity {
  account_id?: string;
  category_id: string;
  asset_model_id: string;
  asset_model_name?: string;
}

export type ProblemCategoryAssetModel = IssueCategoryAssetModel;

export const issueCategoryFormSchema = z.object({
  name: z
    .string()
    .trim()
    .min(1, "common:validation.nameRequired")
    .max(100, "common:validation.nameMaxLength100"),
  description: z
    .string()
    .max(500, "common:validation.descriptionMax500")
    .optional()
    .or(z.literal("")),
  /** MVP: exactly one Service Team per Problem Category. */
  queue_id: z.string().min(1, "pages:issueCategories.form.validation.queueRequired"),
  /** Kept for backward-compat with anything still reading the multi-select shape — derive from queue_id. */
  queue_ids: z.array(z.string()).default([]),
  asset_model_ids: z.array(z.string()).default([]),
  is_active: z.boolean().default(true),
});

export type IssueCategoryFormData = z.infer<typeof issueCategoryFormSchema>;

// Outgoing email status
export type OutgoingEmailStatus = "QUEUED" | "SENDING" | "SENT" | "FAILED";

export interface OutgoingEmail extends BaseEntity {
  account_id: string;
  ticket_id?: string;
  event_type: QueueEmailTemplateEventType | "SYSTEM";

  to: string[];
  cc?: string[];

  subject: string;
  html_body: string;

  status: OutgoingEmailStatus;
  error_message?: string;
  attempts: number;
  max_attempts: number;

  queued_at: Date;
  sent_at?: Date;
  last_attempt_at?: Date;

  triggered_by_user_id?: string;
  queue_id?: string;
  queue_name?: string;
}

export interface EmailSendingConfig {
  webhook_url?: string;
  from_name: string;
  from_email: string;
  reply_to_email?: string;
  enabled: boolean;
  log_only_mode: boolean;
}

export const DEFAULT_EMAIL_CONFIG: EmailSendingConfig = {
  from_name: "LuryOS",
  from_email: "noreply@luryos.app",
  enabled: false,
  log_only_mode: true,
};

export interface EmailSendRequest {
  to: string[];
  cc?: string[];
  subject: string;
  html: string;
  from_name: string;
  from_email: string;
  reply_to?: string;

  ticket_id?: string;
  event_type: string;
  outgoing_email_id: string;
}

export interface EmailSendResponse {
  success: boolean;
  message_id?: string;
  error?: string;
}
